import { useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { ConfigProvider, theme as antdTheme } from 'antd'
import type { RootState } from '../store'
import type { ThemeConfig } from 'antd'

const getIsDark = (theme: 'light' | 'dark' | 'auto') => {
  if (theme === 'dark') return true
  if (theme === 'light') return false
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
}

interface ThemeConfigWrapperProps {
  children: React.ReactNode
  locale: React.ComponentProps<typeof ConfigProvider>['locale']
  baseTheme: ThemeConfig
}

export default function ThemeConfigWrapper({ children, locale, baseTheme }: ThemeConfigWrapperProps) {
  const themeMode = useSelector((state: RootState) => state.ui.theme)
  const isDark = useMemo(() => getIsDark(themeMode), [themeMode])

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const root = document.documentElement
      if (isDark) {
        root.classList.add('dark')
      } else {
        root.classList.remove('dark')
      }
    }

    if (typeof window !== 'undefined') {
      window.localStorage.setItem('theme', themeMode)
    }
  }, [themeMode, isDark])

  const theme: ThemeConfig = useMemo(() => {
    const algorithm = isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm
    return {
      ...baseTheme,
      algorithm,
      token: {
        ...baseTheme.token,
        ...(isDark ? {} : { colorBgContainer: '#ffffff' }),
      },
    }
  }, [baseTheme, isDark])

  return (
    <ConfigProvider locale={locale} theme={theme}>
      {children}
    </ConfigProvider>
  )
}
